/**
 * Supabase Inventory API Service
 *
 * Handles all inventory operations using Supabase.
 */

import { supabase, uploadFile, getPublicUrl } from './client';
import type { InventoryItem, InventoryItemInsert, InventoryItemUpdate } from './database.types';

// Bucket name for inventory images
const INVENTORY_BUCKET = 'inventory';

// Frontend-facing inventory item type (matches existing API interface)
export interface InventoryItemData {
  id: string;
  name: string;
  sku?: string;
  brand?: string;
  quantity: number;
  minStock?: number;
  unitPrice?: number;
  category?: string;
  location?: string;
  productPic?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Map database row to frontend type
const mapInventoryItem = (item: InventoryItem): InventoryItemData => ({
  id: item.id,
  name: item.name,
  sku: item.sku || undefined,
  brand: item.brand || undefined,
  quantity: item.quantity,
  minStock: item.min_stock,
  unitPrice: item.unit_price || undefined,
  category: item.category || undefined,
  location: item.location || undefined,
  productPic: item.product_pic_url || undefined,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
});

// Map frontend data to database insert format
const mapToInsert = (data: Omit<InventoryItemData, 'id' | 'createdAt' | 'updatedAt'>): InventoryItemInsert => ({
  name: data.name,
  sku: data.sku || null,
  brand: data.brand || null,
  quantity: data.quantity || 0,
  min_stock: data.minStock || 0,
  unit_price: data.unitPrice || null,
  category: data.category || null,
  location: data.location || null,
  product_pic_url: data.productPic || null,
});

// Map frontend data to database update format
const mapToUpdate = (data: Partial<Omit<InventoryItemData, 'id' | 'createdAt' | 'updatedAt'>>): InventoryItemUpdate => {
  const update: InventoryItemUpdate = {};

  if (data.name !== undefined) update.name = data.name;
  if (data.sku !== undefined) update.sku = data.sku || null;
  if (data.brand !== undefined) update.brand = data.brand || null;
  if (data.quantity !== undefined) update.quantity = data.quantity;
  if (data.minStock !== undefined) update.min_stock = data.minStock;
  if (data.unitPrice !== undefined) update.unit_price = data.unitPrice || null;
  if (data.category !== undefined) update.category = data.category || null;
  if (data.location !== undefined) update.location = data.location || null;
  if (data.productPic !== undefined) update.product_pic_url = data.productPic || null;

  return update;
};

class InventoryApi {
  /**
   * Get all inventory items
   */
  async getAll(): Promise<{ data: InventoryItemData[]; total: number }> {
    try {
      const { data, error, count } = await supabase
        .from('inventory')
        .select('*', { count: 'exact' })
        .order('name', { ascending: true });

      if (error) {
        // console.error('Failed to fetch inventory:', error);
        throw error;
      }

      const items = (data || []).map(mapInventoryItem);

      return {
        data: items,
        total: count || items.length,
      };
    } catch (error) {
      // console.error('Failed to fetch inventory:', error);
      throw error;
    }
  }

  /**
   * Get inventory item by ID
   */
  async getById(id: string): Promise<InventoryItemData> {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        // console.error('Failed to fetch inventory item:', error);
        throw error;
      }

      return mapInventoryItem(data);
    } catch (error) {
      // console.error('Failed to fetch inventory item:', error);
      throw error;
    }
  }

  /**
   * Create a new inventory item
   */
  async create(data: Omit<InventoryItemData, 'id' | 'createdAt' | 'updatedAt'>): Promise<InventoryItemData> {
    try {
      const insertData = mapToInsert(data);

      const { data: result, error } = await supabase
        .from('inventory')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        // console.error('Failed to create inventory item:', error);
        throw error;
      }

      return mapInventoryItem(result);
    } catch (error) {
      // console.error('Failed to create inventory item:', error);
      throw error;
    }
  }

  /**
   * Update an inventory item
   */
  async update(id: string, data: Partial<Omit<InventoryItemData, 'id' | 'createdAt' | 'updatedAt'>>): Promise<InventoryItemData> {
    try {
      const updateData = mapToUpdate(data);

      const { data: result, error } = await supabase
        .from('inventory')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        // console.error('Failed to update inventory item:', error);
        throw error;
      }

      return mapInventoryItem(result);
    } catch (error) {
      // console.error('Failed to update inventory item:', error);
      throw error;
    }
  }

  /**
   * Delete an inventory item
   */
  async delete(id: string): Promise<void> {
    try {
      // First get the item to check for product pic to delete
      const { data: item } = await supabase
        .from('inventory')
        .select('product_pic_url')
        .eq('id', id)
        .single();

      // Delete the product pic from storage if it exists
      if (item?.product_pic_url) {
        try {
          // Extract the path from the URL
          const url = new URL(item.product_pic_url);
          const pathMatch = url.pathname.match(/\/storage\/v1\/object\/public\/inventory\/(.+)/);
          if (pathMatch) {
            await supabase.storage.from(INVENTORY_BUCKET).remove([pathMatch[1]]);
          }
        } catch (storageError) {
          // console.warn('Failed to delete product image:', storageError);
          // Continue with item deletion even if image deletion fails
        }
      }

      const { error } = await supabase
        .from('inventory')
        .delete()
        .eq('id', id);

      if (error) {
        // console.error('Failed to delete inventory item:', error);
        throw error;
      }
    } catch (error) {
      // console.error('Failed to delete inventory item:', error);
      throw error;
    }
  }

  /**
   * Upload a product picture and link it to an inventory item
   */
  async uploadProductPic(file: File, itemId: string): Promise<string> {
    try {
      // Upload to Supabase storage with compression
      const result = await uploadFile(INVENTORY_BUCKET, 'products', file, {
        compress: true,
        compressionOptions: {
          maxWidth: 800,
          maxHeight: 800,
          quality: 0.85,
          format: 'webp',
        },
        isPublic: true,
      });

      // Update the inventory item with the new image URL
      await supabase
        .from('inventory')
        .update({ product_pic_url: result.url })
        .eq('id', itemId);

      return result.url;
    } catch (error) {
      // console.error('Failed to upload product picture:', error);
      throw error;
    }
  }

  /**
   * Upload a product picture without linking (for use during create)
   * Returns the URL to be included in the create data
   */
  async uploadProductPicOnly(file: File): Promise<{ url: string }> {
    try {
      // Upload to Supabase storage with compression
      const result = await uploadFile(INVENTORY_BUCKET, 'products', file, {
        compress: true,
        compressionOptions: {
          maxWidth: 800,
          maxHeight: 800,
          quality: 0.85,
          format: 'webp',
        },
        isPublic: true,
      });

      return { url: result.url };
    } catch (error) {
      // console.error('Failed to upload product picture:', error);
      throw error;
    }
  }

  /**
   * Search inventory items
   */
  async search(query: string): Promise<InventoryItemData[]> {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .or(`name.ilike.%${query}%,sku.ilike.%${query}%,brand.ilike.%${query}%,category.ilike.%${query}%`)
        .order('name', { ascending: true })
        .limit(50);

      if (error) {
        // console.error('Failed to search inventory:', error);
        throw error;
      }

      return (data || []).map(mapInventoryItem);
    } catch (error) {
      // console.error('Failed to search inventory:', error);
      throw error;
    }
  }

  /**
   * Get low stock items (quantity <= minStock)
   */
  async getLowStock(): Promise<InventoryItemData[]> {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .filter('quantity', 'lte', supabase.rpc('get_min_stock_column'))
        .order('quantity', { ascending: true });

      // Fallback: fetch all and filter client-side if RPC doesn't exist
      if (error) {
        const { data: allData, error: allError } = await supabase
          .from('inventory')
          .select('*')
          .order('quantity', { ascending: true });

        if (allError) throw allError;

        return (allData || [])
          .filter((item) => item.quantity <= item.min_stock)
          .map(mapInventoryItem);
      }

      return (data || []).map(mapInventoryItem);
    } catch (error) {
      // console.error('Failed to fetch low stock items:', error);
      throw error;
    }
  }

  /**
   * Update stock quantity
   */
  async updateQuantity(id: string, quantity: number): Promise<InventoryItemData> {
    return this.update(id, { quantity });
  }

  /**
   * Bulk update stock quantities
   */
  async bulkUpdateQuantities(updates: Array<{ id: string; quantity: number }>): Promise<void> {
    try {
      // Use a transaction-like approach with Promise.all
      await Promise.all(
        updates.map(({ id, quantity }) =>
          supabase
            .from('inventory')
            .update({ quantity })
            .eq('id', id)
        )
      );
    } catch (error) {
      // console.error('Failed to bulk update quantities:', error);
      throw error;
    }
  }

  /**
   * Get inventory by category
   */
  async getByCategory(category: string): Promise<InventoryItemData[]> {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .eq('category', category)
        .order('name', { ascending: true });

      if (error) {
        // console.error('Failed to fetch inventory by category:', error);
        throw error;
      }

      return (data || []).map(mapInventoryItem);
    } catch (error) {
      // console.error('Failed to fetch inventory by category:', error);
      throw error;
    }
  }

  /**
   * Get unique categories
   */
  async getCategories(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('category')
        .not('category', 'is', null);

      if (error) {
        // console.error('Failed to fetch categories:', error);
        throw error;
      }

      const categories = [...new Set(data?.map((item) => item.category).filter(Boolean))];
      return categories.sort() as string[];
    } catch (error) {
      // console.error('Failed to fetch categories:', error);
      throw error;
    }
  }
}

export const inventoryApi = new InventoryApi();
